import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { reportBySlug } from "@/content/research/registry";
import { chartSvg } from "@/lib/researchChart";
import { useSeo } from "@/lib/seo";
import { trackPage } from "@/lib/track";
import "@/styles/home.css";

const SITE = "https://99tests.com";

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a className="btn sm blue" href="/pricing">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru</div>
      <div>
        <a href="/pricing">Pricing</a>
        <a href="/devices">Devices</a>
        <a href="/research" data-testid="footer-research">Research</a>
        <a href="/sample-report/">Sample report</a>
      </div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/">Home</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

function CopyBlock({ code, testid }) {
  const [done, setDone] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(code);
      setDone(true);
      setTimeout(() => setDone(false), 1600);
    } catch (e) {}
  }
  return (
    <div style={{ position: "relative", margin: "10px 0 6px" }}>
      <pre data-testid={testid} style={{ background: "var(--ink)", color: "#fff", padding: "14px 16px", paddingRight: 92, borderRadius: 10, overflowX: "auto", lineHeight: 1.5, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{code}</pre>
      <button type="button" onClick={copy} data-testid={`${testid}-copy`}
        style={{ position: "absolute", top: 10, right: 10, border: "1px solid rgba(255,255,255,.3)", background: "rgba(255,255,255,.12)", color: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 13, cursor: "pointer" }}>
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function inline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
  );
}

function renderMarkdown(md) {
  const lines = (md || "").split("\n");
  const out = [];
  let list = null;
  let k = 0;
  const flush = () => {
    if (list) { out.push(<ul key={`ul${k++}`} style={{ margin: "8px 0 14px 20px" }}>{list}</ul>); list = null; }
  };
  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); return; }
    if (line.startsWith("## ")) { flush(); out.push(<h3 key={`h${k++}`} style={{ marginTop: 22, marginBottom: 8 }}>{inline(line.slice(3))}</h3>); return; }
    if (line.startsWith("- ")) { if (!list) list = []; list.push(<li key={`li${k++}`} style={{ marginBottom: 6 }}>{inline(line.slice(2))}</li>); return; }
    flush();
    out.push(<p key={`p${k++}`} className="lede" style={{ margin: "0 0 12px" }}>{inline(line)}</p>);
  });
  flush();
  return out;
}

function StatTile({ stat, idx }) {
  return (
    <div data-testid={`research-stat-${idx}`} style={{ border: "1px solid var(--line)", borderRadius: 12, padding: "18px 18px", background: "#fff" }}>
      <div style={{ fontSize: 34, fontWeight: 800, color: "var(--ink)", lineHeight: 1 }}>{stat.value}{stat.unit}</div>
      <div style={{ marginTop: 8, fontSize: 14, color: "var(--ink)" }}>{stat.label}</div>
      <div className="muted small" style={{ marginTop: 6 }}>{[stat.sampleSize, stat.period].filter(Boolean).join(" · ")}</div>
    </div>
  );
}

function ChartBlock({ report, chart }) {
  const [show, setShow] = useState(false);
  const svg = chartSvg(chart, { width: 600, height: 400 });
  const pageUrl = `${SITE}/research/${report.slug}`;
  const snippet =
    `<iframe src="${SITE}/embed/research/${report.slug}/${chart.id}" width="600" height="400" loading="lazy" title="${chart.title}"></iframe>\n` +
    `<p>Source: <a href="${pageUrl}">99tests ${report.title}</a></p>`;
  const pngPath = `/research-png/${report.slug}__${chart.id}.png`;
  return (
    <figure data-testid={`research-chart-${chart.id}`} style={{ margin: "0 0 40px" }}>
      <div style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 16, background: "#fff", maxWidth: 600 }}
        dangerouslySetInnerHTML={{ __html: svg }} />
      <figcaption className="muted small" style={{ marginTop: 8, maxWidth: 600 }}>{chart.caption}</figcaption>
      <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        <button type="button" onClick={() => setShow((s) => !s)} data-testid={`embed-toggle-${chart.id}`}
          style={{ border: "1px solid var(--line)", background: "#fff", borderRadius: 8, padding: "7px 14px", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
          {"\nEmbed this chart"}
        </button>
        <a href={pngPath} download data-testid={`png-${chart.id}`}
          style={{ border: "1px solid var(--line)", background: "#fff", borderRadius: 8, padding: "7px 14px", fontSize: 14, textDecoration: "none", color: "var(--ink)", fontWeight: 600 }}>
          Download PNG
        </a>
      </div>
      {show && <div style={{ maxWidth: 600 }}><CopyBlock code={snippet} testid={`embed-snippet-${chart.id}`} /></div>}
    </figure>
  );
}

function NotFound() {
  useSeo({ title: "Report not found", path: "/research", noindex: true });
  return (
    <div className="app-main"><div className="wrap"><h1>Report not found</h1>
      <p><a className="btn" href="/research">All research</a></p></div></div>
  );
}

export default function ResearchReport() {
  const { slug } = useParams();
  const report = reportBySlug(slug);
  const path = `/research/${slug}`;

  const noindex = !report || !report.published;
  const stat0 = report && report.headlineStats && report.headlineStats[0];
  const lead = stat0
    ? `${stat0.label}: ${stat0.value}${stat0.unit} (based on ${stat0.sampleSize}${stat0.period ? `, ${stat0.period}` : ""}).`
    : "";

  const jsonld = report
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: report.title,
          description: report.summary,
          datePublished: report.datePublished,
          dateModified: report.dateModified || report.datePublished,
          author: { "@type": "Person", name: "Praveen Singh" },
          publisher: {
            "@type": "Organization",
            name: "99tests",
            logo: { "@type": "ImageObject", url: `${SITE}/assets/og-99tests.png` },
          },
          mainEntityOfPage: SITE + path,
        },
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: report.title,
          description: report.summary,
          temporalCoverage: report.temporalCoverage || (report.datePublished || "").slice(0, 4),
          spatialCoverage: "India",
          keywords: ["Google Play reviews", "app quality", "India", "UPI", "mobile banking"],
          creator: { "@type": "Organization", name: "99tests", url: SITE },
          license: "https://creativecommons.org/licenses/by/4.0/",
          isAccessibleForFree: true,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
            { "@type": "ListItem", position: 2, name: "Research", item: SITE + "/research" },
            { "@type": "ListItem", position: 3, name: report.title, item: SITE + path },
          ],
        },
      ]
    : null;

  useSeo({
    title: report ? report.title : "Report",
    description: report ? report.summary : undefined,
    path,
    noindex,
    jsonld,
    image: "/assets/og-99tests.png",
  });
  useEffect(() => { trackPage(path); }, [path]);

  if (!report) return <NotFound />;

  const citation = `99tests (${(report.datePublished || "").slice(0, 4)}). ${report.title}. Retrieved from ${SITE}${path}`;

  return (
    <>
      <Header />
      <main data-testid="research-report">
        <section className="hero"><div className="wrap"><div>
          <div className="ref"><b>99tests Research</b>{!report.published && <><span className="dot"></span><span>Draft preview — not yet published</span></>}</div>
          <h1 data-testid="report-title">{report.title}</h1>
          <p className="lede" data-testid="report-subtitle" style={{ fontWeight: 600 }}>{report.subtitle}</p>
          <p className="lede" data-testid="report-lead">{lead}</p>
        </div></div></section>

        <section><div className="wrap">
          <div data-testid="report-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            {(report.headlineStats || []).map((s, i) => <StatTile key={i} stat={s} idx={i} />)}
          </div>
        </div></section>

        <section className="band"><div className="wrap">
          <div className="section-head"><span className="eyebrow">Findings</span><h2>The charts.</h2></div>
          {(report.charts || []).map((c) => <ChartBlock key={c.id} report={report} chart={c} />)}
        </div></section>

        <section><div className="wrap narrow" style={{ marginLeft: 0 }}>
          <div className="section-head"><span className="eyebrow">Use it</span><h2>Cite this report.</h2></div>
          <div data-testid="cite-box" style={{ border: "1px solid var(--line)", borderRadius: 12, padding: "16px 18px", background: "#f7f9fc" }}>
            <p className="muted small" style={{ marginTop: 0 }}>Suggested citation</p>
            <CopyBlock code={citation} testid="cite-text" />
            <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>Page URL: <a href={SITE + path}>{SITE + path}</a></p>
          </div>
        </div></section>

        <section className="band"><div className="wrap narrow" style={{ marginLeft: 0 }}>
          <div className="section-head"><span className="eyebrow">Methodology</span><h2>How we got these numbers.</h2></div>
          <div data-testid="report-methodology">{renderMarkdown(report.methodology)}</div>
          <p className="muted" data-testid="report-sample" style={{ marginTop: 18 }}>{report.sampleDescription}</p>
          <p className="muted small" style={{ marginTop: 8 }}>Licence: {report.license}</p>
        </div></section>

        <section style={{ borderTop: 0 }}><div className="wrap"><div className="cta" data-testid="report-media">
          <div><h2>Media enquiries</h2><p>Questions, interviews or custom cuts of the data: <a href="mailto:hello@99tests.com">hello@99tests.com</a></p></div>
          <a className="btn" href="/assets/og-99tests.png" download data-testid="logo-download">Download 99tests logo (PNG)</a>
        </div></div></section>
      </main>
      <Footer />
    </>
  );
}
